import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Toast, TOAST_DURATION_MS, PERSISTENT, useModalStack } from '@agendor/ui';

export type ToastType = 'success' | 'error' | 'warning';
export type ToastContextType = 'app' | 'modal';

interface ToastContextValue {
    showToast: (message: string, type?: ToastType, context?: ToastContextType) => void;
    modalToast: { message: string | null; type: ToastType; key: number };
    closeModalToast: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}

interface ToastState {
    message: string | null;
    type: ToastType;
    key: number;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [appToast, setAppToast] = useState<ToastState>({ message: null, type: 'error', key: 0 });
    const [modalToast, setModalToast] = useState<ToastState>({ message: null, type: 'error', key: 0 });

    const appTimerRef = useRef<number | null>(null);
    const modalTimerRef = useRef<number | null>(null);
    const appKeyRef = useRef<number>(0);
    const modalKeyRef = useRef<number>(0);

    const showToast = useCallback((msg: string, type: ToastType = 'error', context: ToastContextType = 'app') => {
        if (context === 'app') {
            if (appTimerRef.current) window.clearTimeout(appTimerRef.current);
            appKeyRef.current += 1;
            setAppToast({ message: msg, type, key: appKeyRef.current });
            if (!PERSISTENT) {
                appTimerRef.current = window.setTimeout(
                    () => setAppToast((prev) => ({ ...prev, message: null })),
                    TOAST_DURATION_MS,
                );
            }
        } else {
            if (modalTimerRef.current) window.clearTimeout(modalTimerRef.current);
            modalKeyRef.current += 1;
            setModalToast({ message: msg, type, key: modalKeyRef.current });
            if (!PERSISTENT) {
                modalTimerRef.current = window.setTimeout(
                    () => setModalToast((prev) => ({ ...prev, message: null })),
                    TOAST_DURATION_MS,
                );
            }
        }
    }, []);

    const closeAppToast = useCallback(() => {
        if (appTimerRef.current) window.clearTimeout(appTimerRef.current);
        setAppToast((prev) => ({ ...prev, message: null }));
    }, []);

    const closeModalToast = useCallback(() => {
        if (modalTimerRef.current) window.clearTimeout(modalTimerRef.current);
        setModalToast((prev) => ({ ...prev, message: null }));
    }, []);

    useEffect(() => {
        return () => {
            if (appTimerRef.current) window.clearTimeout(appTimerRef.current);
            if (modalTimerRef.current) window.clearTimeout(modalTimerRef.current);
        };
    }, []);

    const { hasOpenEntry } = useModalStack();
    useEffect(() => {
        if (!hasOpenEntry) closeModalToast();
    }, [hasOpenEntry, closeModalToast]);

    return (
        <ToastContext.Provider value={{ showToast, modalToast, closeModalToast }}>
            {children}
            <Toast
                message={appToast.message}
                type={appToast.type}
                onClose={closeAppToast}
                footerId="app-footer"
                toastKey={appToast.key}
            />
        </ToastContext.Provider>
    );
}
